<script lang="ts">
  import type { Snippet } from 'svelte';

  let {
    variant = 'filled',
    size = 'md',
    iconOnly = false,
    loading = false,
    disabled = false,
    type = 'button',
    href,
    onclick,
    children,
    class: className = '',
    ariaLabel
  }: {
    variant?: 'filled' | 'outline' | 'plain' | 'danger' | 'link';
    size?: 'sm' | 'md' | 'lg';
    iconOnly?: boolean;
    loading?: boolean;
    disabled?: boolean;
    type?: 'button' | 'submit' | 'reset';
    href?: string;
    onclick?: (e: MouseEvent) => void;
    children: Snippet;
    class?: string;
    ariaLabel?: string;
  } = $props();

  const isDisabled = $derived(disabled || loading);
</script>

{#if href}
  <a
    {href}
    class="btn {className}"
    data-variant={variant}
    data-size={size}
    data-icon-only={iconOnly || undefined}
    data-loading={loading || undefined}
    data-disabled={isDisabled || undefined}
    role="button"
    aria-disabled={isDisabled}
    aria-busy={loading}
    aria-label={ariaLabel}
    onclick={isDisabled ? undefined : onclick}
  >
    {#if loading}
      <span class="spinner" aria-hidden="true"></span>
    {/if}
    {@render children()}
  </a>
{:else}
  <button
    {type}
    class="btn {className}"
    data-variant={variant}
    data-size={size}
    data-icon-only={iconOnly || undefined}
    data-loading={loading || undefined}
    {disabled}
    aria-busy={loading}
    aria-label={ariaLabel}
    {onclick}
  >
    {#if loading}
      <span class="spinner" aria-hidden="true"></span>
    {/if}
    {@render children()}
  </button>
{/if}

<style>
  .btn {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    gap: var(--space-2);
    font-family: inherit;
    font-weight: var(--weight-semibold);
    line-height: 1;
    text-decoration: none;
    text-transform: uppercase;
    letter-spacing: 0.04em;
    cursor: pointer;
    border: var(--border-width) solid transparent;
    border-radius: var(--radius-sm);
    transition:
      background var(--dur-fast) var(--ease-out),
      border-color var(--dur-fast) var(--ease-out),
      color var(--dur-fast) var(--ease-out),
      box-shadow var(--dur-fast) var(--ease-out),
      transform var(--dur-instant) var(--ease-out);
  }

  .btn:focus-visible {
    outline: var(--focus-ring-width) solid var(--color-focus-ring);
    outline-offset: var(--focus-ring-offset);
  }

  .btn:not([data-disabled]):active {
    transform: scale(0.98);
  }

  .btn[data-disabled] {
    opacity: 0.5;
    cursor: not-allowed;
    pointer-events: none;
  }

  /* ---- Sizes (escala vanilla: 32 / 40 / 44) ---- */
  .btn[data-size='sm'] {
    height: 32px;
    padding: 0 var(--space-3);
    font-size: 0.72rem;
    gap: var(--space-1);
  }

  .btn[data-size='md'] {
    height: 40px;
    padding: 0 var(--space-4);
    font-size: var(--text-sm);
  }

  .btn[data-size='lg'] {
    height: 44px;
    padding: 0 var(--space-5);
    font-size: var(--text-body);
  }

  .btn[data-icon-only] {
    padding: 0;
    aspect-ratio: 1 / 1;
    text-transform: none;
    letter-spacing: 0;
  }

  .btn[data-icon-only][data-size='sm'] { width: 32px; }
  .btn[data-icon-only][data-size='md'] { width: 40px; }
  .btn[data-icon-only][data-size='lg'] { width: 44px; }

  /* ---- Variants ----
     Naming semántico por rol, no por jerarquía abstracta (F4b §3-5):
       filled  → CTA principal (relleno sólido de marca)
       outline → acción secundaria (borde sin relleno)
       plain   → acción terciaria (solo texto/icono)
       danger  → destructiva (rojo de peligro)
       link    → navegación inline */
  .btn[data-variant='filled'] {
    background: var(--color-accent);
    color: var(--color-accent-fg);
  }
  .btn[data-variant='filled']:not([data-disabled]):hover {
    background: var(--color-accent-hover);
    transform: translateY(-1px);
    box-shadow: var(--shadow-md);
  }

  .btn[data-variant='outline'] {
    background: var(--color-surface);
    color: var(--color-text-primary);
    border-color: var(--color-border-strong);
  }
  .btn[data-variant='outline']:not([data-disabled]):hover {
    background: var(--color-surface-hover);
  }

  .btn[data-variant='plain'] {
    background: transparent;
    color: var(--color-text-secondary);
  }
  .btn[data-variant='plain']:not([data-disabled]):hover {
    background: var(--color-surface-hover);
    color: var(--color-text-primary);
  }

  .btn[data-variant='danger'] {
    background: var(--color-danger);
    color: var(--color-danger-fg);
  }
  .btn[data-variant='danger']:not([data-disabled]):hover {
    background: color-mix(in oklch, var(--color-danger) 90%, black);
  }

  .btn[data-variant='link'] {
    background: transparent;
    color: var(--color-accent);
    padding: 0;
    height: auto;
  }
  .btn[data-variant='link']:not([data-disabled]):hover {
    text-decoration: underline;
    color: var(--color-accent-hover);
  }

  /* ---- Spinner ---- */
  .spinner {
    display: inline-block;
    width: 14px;
    height: 14px;
    border: 2px solid currentColor;
    border-right-color: transparent;
    border-radius: 50%;
    animation: btn-spin 700ms linear infinite;
  }

  @keyframes btn-spin {
    to { transform: rotate(360deg); }
  }
</style>